import React, { useState, useEffect, useMemo } from 'react';
import { ordersAPI, dispatchAPI } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { getToday } from '@/lib/utils';
import {
  Package, 
  MapPin, 
  AlertTriangle, 
  CheckCircle, 
  TrendingDown,
  Calendar,
  Truck,
  Filter,
  Clock,
  Edit3,
  Save,
  ArrowLeft,
  Store,
  ChevronRight,
  Copy,
  Send,
  Users,
  Printer
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import ShipmentSection from './ShipmentSection';
import PrintTicket from '@/components/PrintTicket';
import { PedidosPorLocal } from './consolidado/PedidosPorLocal';

const BodegaDashboardPage = () => {
  const { warehouseId } = useAuth();
  const [selectedDate, setSelectedDate] = useState(getToday());
  const [orders, setOrders] = useState([]);
  const [pendingDispatches, setPendingDispatches] = useState([]);
  const [consolidated, setConsolidated] = useState(null);
  const [locationsStatus, setLocationsStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('shipments');
  const [selectedSupplier, setSelectedSupplier] = useState('all');
  
  // Estado para vista de local seleccionado
  const [selectedLocalOrder, setSelectedLocalOrder] = useState(null);
  
  // Estado para el modal de despacho
  const [dispatchDialogOpen, setDispatchDialogOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [dispatchQuantities, setDispatchQuantities] = useState({});
  const [dispatchNotes, setDispatchNotes] = useState('');
  const [saving, setSaving] = useState(false);
  
  // Estado para impresión de pedido
  const [printOrderData, setPrintOrderData] = useState(null);

  useEffect(() => {
    loadData();
  }, [selectedDate, warehouseId]);

  // Resetear selección al cambiar fecha
  useEffect(() => {
    setSelectedLocalOrder(null);
  }, [selectedDate]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [ordersRes, consolidatedRes, dispatchRes, locationsRes] = await Promise.all([
        ordersAPI.getByDate(selectedDate, warehouseId),
        ordersAPI.getConsolidated(selectedDate, warehouseId),
        dispatchAPI.getPending(selectedDate),
        ordersAPI.getLocationsStatus(selectedDate, warehouseId)
      ]);
      setOrders(ordersRes.data);
      setConsolidated(consolidatedRes.data);
      setPendingDispatches(dispatchRes.data);
      setLocationsStatus(locationsRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  // Función para obtener el despacho de una orden
  const getDispatchForOrder = (orderId) => {
    return pendingDispatches.find(d => d.id === orderId);
  };

  // Cálculos memoizados para rendimiento
  const totalPedido = useMemo(() => {
    if (!selectedOrder?.items) return 0;
    return selectedOrder.items.reduce((sum, item) => sum + item.requested_quantity, 0);
  }, [selectedOrder]);
  
  const totalAEnviar = useMemo(() => {
    return Object.values(dispatchQuantities).reduce((sum, qty) => sum + qty, 0);
  }, [dispatchQuantities]);

  // Función para obtener cantidad despachada de un item
  const getDispatchedQuantity = (orderId, itemId) => {
    const orderWithDispatch = getDispatchForOrder(orderId);
    if (orderWithDispatch?.dispatch) {
      const dispatchedItem = orderWithDispatch.dispatch.items.find(i => i.item_id === itemId);
      return dispatchedItem?.dispatched_quantity ?? null;
    }
    return null;
  };

  // Seleccionar un local para ver detalle
  const selectLocal = (order) => {
    setSelectedLocalOrder(order);
  };

  // Volver a la lista de locales
  const backToLocalsList = () => {
    setSelectedLocalOrder(null);
  };

  // Abrir modal de despacho
  const openDispatchDialog = (order) => {
    setSelectedOrder(order);
    const orderDispatch = getDispatchForOrder(order.id);
    
    if (orderDispatch?.dispatch) {
      // Si ya tiene despacho, cargar cantidades existentes
      const quantities = {};
      orderDispatch.dispatch.items.forEach(item => {
        quantities[item.item_id] = item.dispatched_quantity;
      });
      setDispatchQuantities(quantities);
      setDispatchNotes(orderDispatch.dispatch.notes || '');
    } else {
      // Inicializar con las cantidades pedidas (o stock disponible)
      const quantities = {};
      order.items.forEach(item => {
        const stockInfo = consolidated?.items.find(i => i.item_id === item.item_id);
        const availableStock = stockInfo?.warehouse_stock || 0;
        quantities[item.item_id] = Math.min(item.requested_quantity, availableStock);
      });
      setDispatchQuantities(quantities);
      setDispatchNotes('');
    }
    
    setDispatchDialogOpen(true);
  };

  // Actualizar cantidad en el formulario
  const handleQuantityChange = (itemId, value) => {
    setDispatchQuantities(prev => ({
      ...prev,
      [itemId]: Math.max(0, parseFloat(value) || 0)
    }));
  };

  // Guardar despacho
  const saveDispatch = async () => {
    if (!selectedOrder) return;
    
    setSaving(true);
    try {
      const orderDispatch = getDispatchForOrder(selectedOrder.id);
      const dispatchData = {
        order_id: selectedOrder.id,
        items: Object.entries(dispatchQuantities).map(([item_id, dispatched_quantity]) => ({
          item_id,
          dispatched_quantity
        })),
        notes: dispatchNotes
      };
      
      if (orderDispatch?.has_dispatch) {
        await dispatchAPI.update(orderDispatch.dispatch.id, dispatchData);
        toast.success('Despacho actualizado');
      } else {
        await dispatchAPI.create(dispatchData);
        toast.success('Despacho registrado');
      }
      
      setDispatchDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar despacho');
    } finally {
      setSaving(false);
    }
  };

  // Copiar despacho en formato WhatsApp
  const copyDispatchToWhatsApp = () => {
    if (!selectedOrder) return;
    
    const itemsToSend = selectedOrder.items.filter(item => {
      const qty = dispatchQuantities[item.item_id] || 0;
      return qty > 0;
    });
    
    if (itemsToSend.length === 0) {
      toast.error('No hay productos para enviar');
      return;
    }
    
    let content = `*DESPACHO - ${selectedOrder.location_name}*\n`;
    content += `Fecha: ${selectedDate}\n\n`;
    
    itemsToSend.forEach(item => {
      const qty = dispatchQuantities[item.item_id] || 0;
      content += `• ${item.item_name}: *${qty} ${item.unit}*\n`;
    });
    
    content += `\n_Total: ${totalAEnviar} unidades_`;
    
    if (dispatchNotes) {
      content += `\n\n_Nota: ${dispatchNotes}_`;
    }
    
    navigator.clipboard.writeText(content).then(() => {
      toast.success('Despacho copiado al portapapeles (formato WhatsApp)');
    }).catch(() => {
      toast.error('Error al copiar');
    });
  };

  // Preparar e imprimir pedido de un local
  const handlePrintOrder = (order) => {
    if (!order || !order.items || order.items.length === 0) {
      toast.error('Este pedido no contiene productos para imprimir.');
      return;
    }

    // Preparar items usando requested_quantity (lo que el local pidió)
    const printItems = order.items.map(item => ({
      item_name: item.item_name,
      quantity: item.requested_quantity, // IMPORTANTE: usar requested_quantity
      unit: item.unit,
      supplier: item.supplier || null
    }));

    // Obtener nombre de bodega
    const warehouseName = consolidated?.warehouse_name || 'Bodega Central';

    // Resolver nombre del usuario que creó el pedido
    let createdByName = order.created_by_name || order.location_name || 'Usuario';
    // Si created_by_name no existe pero hay created_by (UUID), usar el nombre del local
    if (!order.created_by_name && order.created_by) {
      createdByName = order.location_name || 'Usuario Local';
    }

    setPrintOrderData({
      type: 'order',
      warehouseName: warehouseName,
      locationName: order.location_name,
      orderType: order.order_type, // "cocina" o "barra"
      createdAt: order.created_at,
      createdBy: createdByName,
      documentId: order.id,
      items: printItems
    });
  };

  // Cerrar modal de impresión
  const closePrintModal = () => {
    setPrintOrderData(null);
  };

  // Calcular items faltantes (donde final_required > 0)
  const missingItems = consolidated?.items.filter(item => item.final_required > 0) || [];
  
  // Extraer proveedores únicos de los items faltantes
  const suppliers = useMemo(() => {
    const uniqueSuppliers = [...new Set(missingItems.map(item => item.supplier || 'Sin asignar'))];
    return uniqueSuppliers.sort();
  }, [missingItems]);

  // Filtrar items faltantes por proveedor
  const filteredMissingItems = useMemo(() => {
    if (selectedSupplier === 'all') return missingItems;
    return missingItems.filter(item => (item.supplier || 'Sin asignar') === selectedSupplier);
  }, [missingItems, selectedSupplier]);

  // Calcular totales
  const totalItemsOrdered = consolidated?.items.reduce((sum, item) => sum + item.total_requested, 0) || 0;
  const totalMissing = missingItems.reduce((sum, item) => sum + item.final_required, 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="bodega-dashboard">
      {/* Header con fecha */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold text-slate-900">Dashboard Bodega</h1>
          <p className="text-muted-foreground mt-1">Gestiona los pedidos y el inventario</p>
        </div>
        <div className="flex items-center gap-3">
          <Calendar className="w-5 h-5 text-muted-foreground" />
          <Input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="w-44"
          />
          <Button onClick={loadData} variant="outline" size="sm">
            Actualizar
          </Button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-lg border border-slate-200 p-5 shadow-sm"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Locales con Pedido</p>
              <p className="text-3xl font-bold text-slate-900 mt-1">
                {locationsStatus?.summary?.with_order || orders.length}
                <span className="text-lg text-slate-400 font-normal">
                  /{locationsStatus?.summary?.total_locations || '?'}
                </span>
              </p>
              {locationsStatus?.summary?.without_order > 0 && (
                <p className="text-xs text-amber-600 mt-1">
                  {locationsStatus.summary.without_order} sin enviar
                </p>
              )}
            </div>
            <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center">
              <MapPin className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-lg border border-slate-200 p-5 shadow-sm"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Productos Solicitados</p>
              <p className="text-3xl font-bold text-slate-900 mt-1">{consolidated?.total_items || 0}</p>
            </div>
            <div className="w-12 h-12 bg-green-50 rounded-lg flex items-center justify-center">
              <Package className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-lg border border-slate-200 p-5 shadow-sm"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Cubiertos con Stock</p>
              <p className="text-3xl font-bold text-green-600 mt-1">
                {(consolidated?.total_items || 0) - missingItems.length}
              </p>
            </div>
            <div className="w-12 h-12 bg-green-50 rounded-lg flex items-center justify-center">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-lg border border-slate-200 p-5 shadow-sm"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Productos Faltantes</p>
              <p className="text-3xl font-bold text-red-600 mt-1">{missingItems.length}</p>
            </div>
            <div className="w-12 h-12 bg-red-50 rounded-lg flex items-center justify-center">
              <AlertTriangle className="w-6 h-6 text-red-600" />
            </div>
          </div>
        </motion.div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-slate-200">
        <button
          onClick={() => setActiveTab('shipments')}
          className={`px-4 py-3 font-medium text-sm border-b-2 transition-colors ${
            activeTab === 'shipments'
              ? 'border-primary text-primary'
              : 'border-transparent text-muted-foreground hover:text-slate-900'
          }`}
          data-testid="shipments-tab"
        >
          <Send className="w-4 h-4 inline mr-2" />
          Envío a Locales
        </button>
        <button
          onClick={() => setActiveTab('by-local')}
          className={`px-4 py-3 font-medium text-sm border-b-2 transition-colors ${
            activeTab === 'by-local'
              ? 'border-primary text-primary'
              : 'border-transparent text-muted-foreground hover:text-slate-900'
          }`}
          data-testid="by-local-tab"
        >
          <Truck className="w-4 h-4 inline mr-2" />
          Pedidos por Local
        </button>
        <button
          onClick={() => setActiveTab('missing')}
          className={`px-4 py-3 font-medium text-sm border-b-2 transition-colors ${
            activeTab === 'missing'
              ? 'border-primary text-primary'
              : 'border-transparent text-muted-foreground hover:text-slate-900'
          }`}
          data-testid="missing-tab"
        >
          <TrendingDown className="w-4 h-4 inline mr-2" />
          Productos Faltantes
          {missingItems.length > 0 && (
            <span className="ml-2 px-2 py-0.5 bg-red-100 text-red-700 text-xs rounded-full">
              {missingItems.length}
            </span>
          )}
        </button>
        <button
          onClick={() => setActiveTab('locations-status')}
          className={`px-4 py-3 font-medium text-sm border-b-2 transition-colors ${
            activeTab === 'locations-status'
              ? 'border-primary text-primary'
              : 'border-transparent text-muted-foreground hover:text-slate-900'
          }`}
          data-testid="locations-status-tab"
        >
          <Users className="w-4 h-4 inline mr-2" />
          Estado Locales
          {locationsStatus?.summary?.without_order > 0 && (
            <span className="ml-2 px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded-full">
              {locationsStatus.summary.without_order} pendientes
            </span>
          )}
        </button>
      </div>

      {/* Content based on active tab */}
      {activeTab === 'by-local' ? (
        <PedidosPorLocal date={selectedDate} warehouseId={warehouseId} onPrint={handlePrintOrder} onChanged={loadData} />
      ) : activeTab === 'missing' && (
        <div className="space-y-4">
          {missingItems.length === 0 ? (
            <div className="bg-white rounded-lg border border-slate-200 p-12 text-center">
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-900">¡Stock Suficiente!</h3>
              <p className="text-muted-foreground mt-1">
                Tienes stock suficiente para cubrir todos los pedidos del día
              </p>
            </div>
          ) : (
            <>
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div className="flex items-center gap-3">
                    <AlertTriangle className="w-6 h-6 text-red-600" />
                    <div>
                      <p className="font-semibold text-red-900">Atención: Productos Faltantes</p>
                      <p className="text-sm text-red-700">
                        Necesitas pedir {missingItems.length} productos a proveedores para completar los pedidos
                      </p>
                    </div>
                  </div>
                  
                  {/* Filtro por proveedor */}
                  <div className="flex items-center gap-2">
                    <Select value={selectedSupplier} onValueChange={setSelectedSupplier}>
                      <SelectTrigger className="w-52 bg-white" data-testid="bodega-supplier-filter">
                        <Filter className="w-4 h-4 mr-2 text-red-600" />
                        <SelectValue placeholder="Filtrar por proveedor" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todos los proveedores</SelectItem>
                        {suppliers.map(supplier => (
                          <SelectItem key={supplier} value={supplier}>{supplier}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Mostrar contador de filtro */}
              {selectedSupplier !== 'all' && (
                <div className="flex items-center justify-between bg-blue-50 border border-blue-200 rounded-lg px-4 py-2">
                  <span className="text-sm text-blue-800">
                    Mostrando {filteredMissingItems.length} de {missingItems.length} productos faltantes de <strong>{selectedSupplier}</strong>
                  </span>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => setSelectedSupplier('all')}
                    className="text-blue-700 hover:text-blue-900"
                  >
                    Ver todos
                  </Button>
                </div>
              )}

              <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
                <table className="w-full">
                  <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
                    <tr>
                      <th className="px-6 py-3 text-left font-semibold">Producto</th>
                      <th className="px-6 py-3 text-left font-semibold">Proveedor</th>
                      <th className="px-6 py-3 text-right font-semibold">Pedido Total</th>
                      <th className="px-6 py-3 text-right font-semibold">Stock Actual</th>
                      <th className="px-6 py-3 text-right font-semibold bg-red-50">Faltante</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredMissingItems.map((item, index) => (
                      <motion.tr
                        key={item.item_id}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: index * 0.03 }}
                        className="border-b hover:bg-slate-50"
                      >
                        <td className="px-6 py-4">
                          <p className="font-medium text-slate-900">{item.item_name}</p>
                          <p className="text-xs text-muted-foreground">{item.item_code} • {item.unit}</p>
                        </td>
                        <td className="px-6 py-4">
                          <span className="inline-flex items-center px-2 py-1 rounded-md bg-blue-50 text-blue-700 text-xs font-medium">
                            {item.supplier || 'Sin asignar'}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-right font-medium">
                          {item.total_requested} {item.unit}
                        </td>
                        <td className="px-6 py-4 text-right text-slate-600">
                          {item.warehouse_stock} {item.unit}
                        </td>
                        <td className="px-6 py-4 text-right bg-red-50">
                          <span className="font-bold text-red-700">
                            {item.final_required} {item.unit}
                          </span>
                        </td>
                      </motion.tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-slate-100">
                    <tr>
                      <td colSpan="4" className="px-6 py-4 text-right font-semibold">
                        {selectedSupplier === 'all' ? 'Total Productos Faltantes:' : `Total ${selectedSupplier}:`}
                      </td>
                      <td className="px-6 py-4 text-right font-bold text-red-700 bg-red-50">
                        {filteredMissingItems.length} productos
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              {/* Desglose por local de productos faltantes */}
              <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-6">
                <h3 className="font-semibold text-slate-900 mb-4">Desglose por Local</h3>
                <div className="space-y-4">
                  {filteredMissingItems.map((item) => (
                    <div key={item.item_id} className="border-b border-slate-100 pb-4 last:border-0 last:pb-0">
                      <div className="flex items-center justify-between mb-2">
                        <p className="font-medium text-slate-900">{item.item_name}</p>
                        <span className="text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded">
                          {item.supplier || 'Sin asignar'}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                        {Object.entries(item.location_breakdown || {}).map(([location, qty]) => (
                          <div key={location} className="bg-slate-50 rounded px-3 py-2 text-sm">
                            <span className="text-muted-foreground">{location}:</span>
                            <span className="font-medium ml-1">{qty}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* Pestaña de Envío a Locales */}
      {activeTab === 'shipments' && (
        <ShipmentSection />
      )}

      {/* Pestaña de Estado de Locales */}
      {activeTab === 'locations-status' && (
        <div className="space-y-4" data-testid="locations-status-content">
          {/* Resumen */}
          <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                <Users className="w-5 h-5 text-primary" />
                Estado de Envío de Pedidos por Local
              </h2>
              <div className="text-sm text-muted-foreground">
                {locationsStatus?.summary?.completion_rate || 0}% completado
              </div>
            </div>
            
            {/* Barra de progreso */}
            <div className="w-full bg-slate-200 rounded-full h-3 mb-4">
              <div 
                className="bg-green-500 h-3 rounded-full transition-all duration-500"
                style={{ width: `${locationsStatus?.summary?.completion_rate || 0}%` }}
              />
            </div>
            
            {/* Stats */}
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center p-3 bg-slate-50 rounded-lg">
                <p className="text-2xl font-bold text-slate-900">{locationsStatus?.summary?.total_locations || 0}</p>
                <p className="text-xs text-muted-foreground">Total Locales</p>
              </div>
              <div className="text-center p-3 bg-green-50 rounded-lg">
                <p className="text-2xl font-bold text-green-600">{locationsStatus?.summary?.with_order || 0}</p>
                <p className="text-xs text-green-700">Con Pedido</p>
              </div>
              <div className="text-center p-3 bg-amber-50 rounded-lg">
                <p className="text-2xl font-bold text-amber-600">{locationsStatus?.summary?.without_order || 0}</p>
                <p className="text-xs text-amber-700">Sin Enviar</p>
              </div>
            </div>
          </div>

          {/* Lista de locales */}
          <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
            <div className="bg-slate-50 px-6 py-3 border-b border-slate-200">
              <p className="text-sm font-medium text-slate-600">
                Locales que aún NO han enviado su pedido se muestran primero
              </p>
            </div>
            <div className="divide-y divide-slate-100">
              {(locationsStatus?.locations || []).map((loc, index) => (
                <motion.div
                  key={loc.location_id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className={`px-6 py-4 flex items-center justify-between ${
                    !loc.has_order ? 'bg-amber-50/50' : ''
                  }`}
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      loc.has_order ? 'bg-green-100' : 'bg-amber-100'
                    }`}>
                      {loc.has_order ? (
                        <CheckCircle className="w-5 h-5 text-green-600" />
                      ) : (
                        <Clock className="w-5 h-5 text-amber-600" />
                      )}
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">{loc.location_name}</p>
                      <p className="text-xs text-muted-foreground">{loc.warehouse_name}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    {loc.has_order ? (
                      <>
                        <p className="font-semibold text-green-600">
                          {loc.total_items} productos
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {loc.total_quantity} unidades • {loc.order_types.join(', ')}
                        </p>
                      </>
                    ) : (
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-amber-100 text-amber-700">
                        Sin enviar
                      </span>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Nota informativa */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-blue-600 mt-0.5" />
              <div>
                <p className="font-medium text-blue-900">Locales sin pedido</p>
                <p className="text-sm text-blue-700 mt-1">
                  Los locales que no han enviado su pedido del día aparecen marcados como "Sin enviar". 
                  Puedes contactarlos para recordarles o considerar su demanda como cero para el día.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Despacho */}
      <Dialog open={dispatchDialogOpen} onOpenChange={setDispatchDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Truck className="w-5 h-5 text-primary" />
              {getDispatchForOrder(selectedOrder?.id)?.has_dispatch ? 'Editar Despacho' : 'Iniciar Despacho'} - {selectedOrder?.location_name}
            </DialogTitle>
          </DialogHeader>
          
          {selectedOrder && (
            <div className="space-y-4">
              {/* Lista de productos */}
              <div className="border rounded-lg overflow-hidden">
                <div className="bg-slate-100 px-4 py-2 grid grid-cols-12 gap-2 text-xs font-semibold text-slate-600 uppercase">
                  <div className="col-span-5">Producto</div>
                  <div className="col-span-2 text-center">Pedido</div>
                  <div className="col-span-2 text-center">Stock</div>
                  <div className="col-span-3 text-center">A Enviar</div>
                </div>
                <div className="divide-y">
                  {selectedOrder.items.map((item) => {
                    const stockInfo = consolidated?.items.find(i => i.item_id === item.item_id);
                    const availableStock = stockInfo?.warehouse_stock || 0;
                    const currentQty = dispatchQuantities[item.item_id] || 0;
                    
                    return (
                      <div key={item.item_id} className="px-4 py-3 grid grid-cols-12 gap-2 items-center">
                        <div className="col-span-5">
                          <p className="font-medium text-slate-900 text-sm">{item.item_name}</p>
                          <p className="text-xs text-muted-foreground">{item.item_code}</p>
                        </div>
                        <div className="col-span-2 text-center">
                          <span className="font-semibold text-slate-700">{item.requested_quantity}</span>
                          <span className="text-xs text-muted-foreground ml-1">{item.unit}</span>
                        </div>
                        <div className="col-span-2 text-center">
                          <span className={`font-medium ${availableStock < item.requested_quantity ? 'text-red-600' : 'text-green-600'}`}>
                            {availableStock}
                          </span>
                        </div>
                        <div className="col-span-3 flex justify-center">
                          <Input
                            type="number"
                            min="0"
                            step="0.5"
                            value={currentQty}
                            onChange={(e) => handleQuantityChange(item.item_id, e.target.value)}
                            className="w-24 text-center"
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              
              {/* Resumen */}
              <div className="bg-slate-50 rounded-lg p-4 flex justify-between items-center">
                <div>
                  <p className="text-sm text-slate-600">Total pedido: <span className="font-semibold">{totalPedido} unidades</span></p>
                </div>
                <div>
                  <p className="text-sm text-primary">Total a enviar: <span className="font-bold">{totalAEnviar} unidades</span></p>
                </div>
              </div>
              
              {/* Notas */}
              <div>
                <Label htmlFor="notes">Notas del despacho (opcional)</Label>
                <Textarea
                  id="notes"
                  value={dispatchNotes}
                  onChange={(e) => setDispatchNotes(e.target.value)}
                  placeholder="Observaciones sobre el despacho..."
                  rows={2}
                  className="mt-1"
                />
              </div>
            </div>
          )}
          
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button 
              variant="outline" 
              onClick={copyDispatchToWhatsApp}
              disabled={totalAEnviar === 0}
              className="text-green-600 border-green-300 hover:bg-green-50"
            >
              <Copy className="w-4 h-4 mr-2" />
              Copiar WhatsApp
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setDispatchDialogOpen(false)} disabled={saving}>
                Cancelar
              </Button>
              <Button onClick={saveDispatch} disabled={saving}>
                {saving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Guardando...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Guardar Despacho
                  </>
                )}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de impresión de pedido */}
      {printOrderData && (
        <PrintTicket
          type={printOrderData.type}
          warehouseName={printOrderData.warehouseName}
          locationName={printOrderData.locationName}
          orderType={printOrderData.orderType}
          createdAt={printOrderData.createdAt}
          createdBy={printOrderData.createdBy}
          documentId={printOrderData.documentId}
          items={printOrderData.items}
          onClose={closePrintModal}
        />
      )}
    </div>
  );
};

export default BodegaDashboardPage;
